import { AlertTriangle } from 'lucide-react';

/**
 * The "here is how to fix it" line on a sensor block's STUDENT surface.
 *
 * Distinct from a feedback state, and deliberately so. A feedback state says what went wrong, is
 * teacher-editable, and fires after submission — by which point the attempt is spent and the reading
 * is gone. This says what to DO about it, and its entire value is in arriving BEFORE the capture is
 * committed, while the student can still walk away from the car park or turn the phone in a figure
 * eight and try again.
 *
 * Not teacher-editable for the same reason: it is the only actionable instruction a student gets,
 * and a teacher rewording their own feedback should not be able to delete it. Nothing else supplies
 * it either — a native app can hand a bad compass to the OS calibration prompt, but a browser has no
 * API to summon that, so if this does not say it, nobody does.
 *
 * Shared by GPS and Compass (and Angle when its states grow advice) so the three cannot drift into
 * three different treatments of the same idea.
 */
export function SensorAdvisory({ label, advice }: {
  /** The condition, already carrying its own punctuation so a locale can place the colon its own
   * way — French, for one, wants a space before it. Omitted where the warning is a plain statement
   * with no separable condition to name (GPS's network-based notice). */
  label?: string;
  /** What to do about it. One sentence: this competes for attention with a live readout. */
  advice: string;
}) {
  return (
    // `status`, not `alert`: in a live-reading mode this sits beside a continuously updating number,
    // and an assertive region would interrupt a screen reader every time the value moved. The text
    // only changes when the reading crosses a threshold, so a polite region announces once.
    <p role='status' className='mx-auto flex w-fit items-start gap-1.5 text-xs text-ws-warning-fg'>
      <AlertTriangle aria-hidden='true' className='mt-0.5 h-3.5 w-3.5 shrink-0' />
      <span>
        {label && <><span className='font-semibold'>{label}</span> </>}
        {advice}
      </span>
    </p>
  );
}
